import { AnimatePresence, motion } from "motion/react"

import { STAGE_SPRING } from "@/components/coach-stage"
import { PageContainer } from "@/components/page-container"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { greetingFor } from "@/lib/greeting"
import { cn } from "@/lib/utils"
import { initialsOf } from "@nl/shared"

import {
  ComposerSlot,
  ProgressPanel,
  RecentList,
  SpeakButton,
  TileRow,
  moduleRows,
  type ImageSet,
  type StartLayoutProps,
} from "./shared"

/**
 * Draft "Poster": a calm header on the approved coast artwork with the coach
 * centred, the progress as one strip below it, the modules as upright posters
 * the way a streaming catalogue shelves them.
 */
export function PosterLayout(p: StartLayoutProps & { set?: ImageSet }) {
  const rows = moduleRows(p.recommendations)
  // The abstract set is light, so the header keeps dark type on a pale wash
  // instead of white type on a scrim.
  const light = p.set === "abstrakt"
  return (
    <>
      <section className="relative w-full overflow-hidden bg-[var(--surface-alt)]">
        <img
          src={light ? "/abstrakt/hero.jpg" : "/fotos/hero-hafen.jpg"}
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover object-bottom dark:opacity-70"
        />
        <div
          aria-hidden
          className={cn(
            "absolute inset-0",
            light
              ? "bg-[linear-gradient(to_bottom,color-mix(in_oklab,var(--paper)_30%,transparent)_0%,transparent_40%,var(--paper)_100%)]"
              : "bg-[linear-gradient(to_bottom,rgba(0,0,0,0.35)_0%,rgba(0,0,0,0.25)_55%,var(--paper)_100%)]"
          )}
        />
        <motion.div
          layoutId="coach-card"
          transition={STAGE_SPRING}
          className={cn(
            "relative z-10 mx-auto flex min-h-[440px] w-full max-w-[680px] flex-col items-center justify-center px-8 pt-14 pb-20 text-center",
            light ? "text-foreground" : "text-white [text-shadow:0_1px_3px_rgba(0,0,0,0.4)]"
          )}
        >
          <AnimatePresence mode="wait" initial={false}>
            {p.voice ? (
              <motion.div
                key="voice"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="w-full overflow-hidden rounded-2xl bg-[var(--paper)] text-left shadow-[0_24px_80px_rgba(31,31,31,0.16)]"
              >
                {p.voice}
              </motion.div>
            ) : (
              <motion.div
                key="prompt"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex w-full flex-col items-center"
              >
                <Avatar className="size-[76px] ring-2 ring-[var(--paper)] shadow-[0_12px_32px_rgba(31,31,31,0.18)]">
                  {p.coach?.portraitUrl ? (
                    <AvatarImage src={p.coach.portraitUrl} alt="" />
                  ) : null}
                  <AvatarFallback className="bg-accent text-[20px] font-medium text-primary">
                    {p.coach ? initialsOf(p.coach.name) : ""}
                  </AvatarFallback>
                </Avatar>
                <div className={cn("mt-3 text-[13px]", light ? "text-muted-foreground" : "text-white/85")}>
                  {p.coachName} · Dein KI-Coach
                </div>
                <h1 className="mt-4 font-display text-[30px] leading-[1.12] font-medium tracking-tight sm:text-[34px]">
                  {greetingFor(new Date())}
                  {p.firstName ? `, ${p.firstName}` : ""}.{" "}
                  <span className={light ? "text-foreground/65" : "text-white/75"}>
                    Woran arbeiten wir heute?
                  </span>
                </h1>
                {p.standHint ? (
                  <p className={cn("mt-2.5 text-[13.5px]", light ? "text-muted-foreground" : "text-white/85")}>
                    {p.standHint}
                  </p>
                ) : null}
                <div className="mt-7 flex w-full flex-col gap-2.5 text-left text-foreground [text-shadow:none] sm:flex-row sm:items-stretch">
                  <ComposerSlot>
                    <div className="rounded-xl bg-[var(--paper)]/85 shadow-[0_12px_40px_rgba(31,31,31,0.12)] backdrop-blur-xl">
                      {p.composer}
                    </div>
                  </ComposerSlot>
                  <SpeakButton coachName={p.coachName} onSpeak={p.onSpeak} tone="outline" />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </section>

      <PageContainer className="-mt-4 space-y-10 pb-14">
        <ProgressPanel
          resume={p.resume}
          completedCount={p.completedCount}
          accent="red"
          compact
        />
        {rows.map((row) => (
          <TileRow key={row.title} row={row} shape="poster" tileWidth="w-[188px]" set={p.set} />
        ))}
        <RecentList
          entries={p.entries}
          isLoading={p.entriesLoading}
          coachName={p.coachName}
        />
      </PageContainer>
    </>
  )
}
